// 人事採用 計画グリッドの共通計算ヘルパー。
// グリッド (RecruitmentPlanGrid) と全社サマリー (ページ) で同じ計算を共有する。
import { MONTH_ORDER } from '../profitAndLoss/constants';
import { editKey, type RecruitmentPlanRow, type PlanEdits } from '../../hooks/useRecruitmentPlan';

// リーフ(支店×部門×役職)1行・1月分の系列点 (実績在籍参照・採用予定・計画在籍)
// 増員計画のため退職は扱わず、採用予定のみを積み上げる。net は当月の採用予定 (= 増員) を指す。
export type MonthSeriesPoint = {
  m: number;
  headcount: number; // 実績在籍参照 (各月末)
  hires: number; // 当月の採用予定 (+)
  net: number; // 当月の増員 (= 採用予定。バナー小計で共有)
  planned: number; // 計画在籍 = 実績在籍参照 + 9月からの採用予定累積
};

/**
 * 支店×部門×役職の1行 (リーフ) について 9〜8月の系列を算出する。
 * 計画在籍[m] = 実績在籍参照[m] + Σ_{k=9..m} 採用予定[k] (増員計画。退職は見込まない)
 */
export function computeDeptSeries(row: RecruitmentPlanRow, edits: PlanEdits): MonthSeriesPoint[] {
  let cumulative = 0;
  return MONTH_ORDER.map((m) => {
    const e = edits[editKey(row.branch, row.department, row.position, m)] || { hires: 0 };
    cumulative += e.hires;
    const headcount = Number(row[`month_${m}_headcount`]) || 0;
    return { m, headcount, hires: e.hires, net: e.hires, planned: headcount + cumulative };
  });
}

// 支店1つ分 (配下部門行の集合) の1月分の集計点
export type BranchSeriesPoint = {
  m: number;
  net: number; // 当月の採用予定合計 (Σ採用 = 増員)
  planned: number; // 計画在籍合計 (配下部門の計画在籍の和)
  headcount: number; // 実績在籍合計
};

/**
 * 支店配下の部門行を合算し、9〜8月の支店レベル系列を算出する。
 * net は当月単月の採用予定合計 (増員)、planned は配下部門の計画在籍 (累積) の和。
 */
export function computeBranchSeries(rows: RecruitmentPlanRow[], edits: PlanEdits): BranchSeriesPoint[] {
  const perDept = rows.map((r) => computeDeptSeries(r, edits));
  return MONTH_ORDER.map((m, idx) => {
    let net = 0;
    let planned = 0;
    let headcount = 0;
    perDept.forEach((series) => {
      const p = series[idx];
      net += p.hires;
      planned += p.planned;
      headcount += p.headcount;
    });
    return { m, net, planned, headcount };
  });
}

/**
 * 表示年度において対象月が確定済み (過去) かを判定する。
 * 会計年度は9月開始のため、9〜12月は fiscalYear-1 年, 1〜8月は fiscalYear 年。
 * 過去年度は全月ロック、将来年度は全月編集可、当年度は当月より前をロックする。
 */
export function isMonthLocked(fiscalYear: number, month: number, now: Date): boolean {
  const calYear = month >= 9 ? fiscalYear - 1 : fiscalYear;
  const nowYear = now.getFullYear();
  const nowMonth = now.getMonth() + 1;
  return calYear < nowYear || (calYear === nowYear && month < nowMonth);
}
